import React, { useEffect, useState } from "react";
import Swal from 'sweetalert2';
import InputMask from 'react-input-mask';
import API from "../../utils/API";
import { useSearchParams, useNavigate } from 'react-router-dom';

export default function VehicleData(props) {
    const [vehicle, setVehicle] = useState({
        license_plate: '',
        model: '',
        year: '',
        color: '',
        fuel_type: '',
        nationality: '',
        city: '',
        armored: false,
        engine: '',
        displacement: '',
        chassi: ''
    });
    const [fieldErrors, setFieldErrors] = useState({});
    const [searchParams] = useSearchParams();
    const id = searchParams.get('id');
    const navigate = useNavigate();
    const [user, setUser] = useState(null);

    useEffect(() => {
        let retries = 10;
        const interval = setInterval(() => {
            const storedUserJSON = localStorage.getItem("yc_usr_dt_ls");
            if (storedUserJSON) {
                try {
                    const parsedUser = JSON.parse(storedUserJSON);
                    setUser(parsedUser);
                    // Mechanic can VIEW an existing service's vehicle (read-only),
                    // but cannot start a new service.
                    if (parsedUser.role === "MECANICO" && !id) {
                        navigate('/services');
                    }
                    clearInterval(interval);
                } catch (error) {
                    console.error("Failed to parse yc_usr_dt_ls:", error);
                    clearInterval(interval);
                }
            } else {
                retries--;
                if (retries <= 0) clearInterval(interval);
            }
        }, 500);
        return () => clearInterval(interval);
    }, [id]);

    const isReadOnly = user?.role === "MECANICO";

    useEffect(() => {
        if (id) {
            Swal.fire({ title: 'Carregando veículo', text: 'Aguarde um momento', showConfirmButton: false, allowOutsideClick: false, didOpen: () => Swal.showLoading() });
            API.getVehicleByService(API.cancelToken(), id).then(res => {
                Swal.close();
                setVehicle({ ...res.data, armored: !!res.data?.armored });
            }).catch(err => {
                Swal.close();
                console.log(err);
            });
        } else {
            // Vehicle is step 2 now — it always needs a service created by the
            // client step. Without an id, send the user back to the client step.
            navigate('/create/service?step=1');
        }
    }, [id]);

    const isValidLicensePlate = (plate) => {
        const normalized = plate.toUpperCase().replace(/[^A-Z0-9]/g, '');
        return /^[A-Z]{3}\d{4}$/.test(normalized) || /^[A-Z]{3}\d[A-Z]\d{2}$/.test(normalized);
    };

    const validateVehicle = () => {
        const requiredFields = ['license_plate', 'model', 'year', 'color', 'fuel_type', 'nationality', 'city'];
        const errors = {};
        requiredFields.forEach(field => {
            if (!vehicle[field]?.toString().trim()) errors[field] = true;
        });
        if (vehicle.license_plate && !isValidLicensePlate(vehicle.license_plate)) {
            errors.license_plate = true;
            Swal.fire({ title: 'Placa inválida', text: 'A placa deve estar no formato AAA1234 ou AAA1A23.', icon: 'warning', confirmButtonText: 'Ok' });
            setFieldErrors(errors);
            return false;
        }
        setFieldErrors(errors);
        if (Object.keys(errors).length > 0) {
            Swal.fire({ title: 'Campos obrigatórios', text: 'Preencha todos os campos antes de prosseguir.', icon: 'warning', confirmButtonText: 'Ok' });
            return false;
        }
        return true;
    };

    const saveVehicle = () => {
        if (!validateVehicle()) return;
        Swal.fire({ title: 'Salvando veículo', text: 'Aguarde um momento', showConfirmButton: false, allowOutsideClick: false, didOpen: () => Swal.showLoading() });
        API.postVehicle(API.cancelToken(), vehicle).then(res => {
            Swal.close();
            localStorage.setItem('vehicle', JSON.stringify(vehicle));
            localStorage.setItem('vehicle_id', JSON.stringify(res.data.vehicle_id));
            API.postServiceStep1(API.cancelToken(), res.data.vehicle_id, id).then(res => {
                navigate('/create/service?step=3&id=' + (res.data.service_id || id));
            });
        }).catch(err => {
            Swal.fire({ title: 'Erro ao salvar veículo', text: 'Tente novamente mais tarde', icon: 'error', confirmButtonText: 'Ok' });
        });
    };

    const handleInputChange = (event) => {
        const { name, value } = event.target;
        setFieldErrors({});
        setVehicle(prev => ({ ...prev, [name]: name === "license_plate" ? value.toUpperCase() : value }));
    };

    const searchVehicle = () => {
        if (vehicle.license_plate.length < 7) return;
        API.getVehicle(API.cancelToken(), vehicle.license_plate).then(res => {
            setVehicle({ ...res.data, armored: !!res.data?.armored });
        }).catch(err => {
            Swal.fire({
                title: 'Veículo não encontrado',
                text: 'Deseja cadastrar um novo veículo?',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Sim',
                cancelButtonText: 'Não',
            }).then((result) => {
                if (result.isConfirmed) {
                    setFieldErrors({});
                    setVehicle({ license_plate: vehicle.license_plate, model: '', year: '', color: '', fuel_type: '', renavam: '', nationality: '', city: '', armored: false, engine: '', displacement: '', chassi: '' });
                }
            });
        });
    };

    const inputClass = (field) =>
        `w-full px-3 py-2.5 border-2 rounded-lg text-sm bg-white transition-all focus:outline-none focus:ring-2 focus:ring-[#389E0D] focus:border-transparent ${
            fieldErrors[field] ? 'border-red-300 bg-red-50' : 'border-gray-200'
        }`;

    return (
        <div className="flex flex-col items-center self-stretch bg-white rounded-lg shadow-sm max-md:max-w-full">
            {/* Header */}
            <div className="w-full px-8 pt-6 pb-4 border-b border-gray-100 max-md:px-5">
                <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-[#389E0D] bg-opacity-10 flex items-center justify-center">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-[#389E0D]" viewBox="0 0 20 20" fill="currentColor">
                            <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
                            <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1v-1h3.05a2.5 2.5 0 014.9 0H19a1 1 0 001-1v-2a4 4 0 00-4-4h-3V5a1 1 0 00-1-1H3zm9 4h3l2 2v1h-5V8z" />
                        </svg>
                    </div>
                    <div>
                        <h2 className="text-xl font-semibold text-gray-900">Dados do Veículo</h2>
                        <p className="text-sm text-gray-500">Preencha as informações do veículo para prosseguir</p>
                    </div>
                </div>
            </div>

            {/* Form */}
            <div className="w-full px-8 py-6 max-md:px-5">
                {/* License plate - highlighted */}
                <div className="mb-6 p-4 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Placa do Veículo
                    </label>
                    <div className="flex gap-3 items-end">
                        <div className="flex-1">
                            <InputMask
                                className={`${inputClass('license_plate')} font-mono text-lg tracking-widest uppercase`}
                                value={vehicle.license_plate}
                                placeholder="ABC1D23"
                                onBlur={isReadOnly ? undefined : searchVehicle}
                                onChange={handleInputChange}
                                name="license_plate"
                                readOnly={isReadOnly}
                            />
                        </div>
                        {!isReadOnly && (
                            <button
                                type="button"
                                onClick={searchVehicle}
                                className="h-[42px] px-4 flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-lg transition-colors"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                                </svg>
                                Buscar
                            </button>
                        )}
                    </div>
                    {!isReadOnly && (
                        <p className="text-xs text-gray-400 mt-2">Digite a placa e clique em buscar para preencher automaticamente</p>
                    )}
                </div>

                {/* Grid fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-600 mb-1.5">Marca / Modelo</label>
                        <input
                            className={inputClass('model')}
                            type="text"
                            name="model"
                            placeholder="Ex: Toyota Corolla"
                            value={vehicle.model}
                            onChange={handleInputChange}
                            readOnly={isReadOnly}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-600 mb-1.5">Ano / Modelo</label>
                        <InputMask
                            className={inputClass('year')}
                            mask="9999 / 9999"
                            placeholder="2020 / 2021"
                            value={vehicle.year}
                            onChange={handleInputChange}
                            name="year"
                            readOnly={isReadOnly}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-600 mb-1.5">Cor</label>
                        <input
                            className={inputClass('color')}
                            type="text"
                            name="color"
                            placeholder="Ex: Prata"
                            value={vehicle.color}
                            onChange={handleInputChange}
                            readOnly={isReadOnly}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-600 mb-1.5">Combustível</label>
                        <select
                            className={inputClass('fuel_type')}
                            name="fuel_type"
                            value={vehicle.fuel_type}
                            onChange={handleInputChange}
                            disabled={isReadOnly}
                        >
                            <option value="">Selecione...</option>
                            <option value="Gasolina">Gasolina</option>
                            <option value="Etanol">Etanol</option>
                            <option value="Flex">Flex</option>
                            <option value="Diesel">Diesel</option>
                            <option value="GNV">GNV</option>
                            <option value="Elétrico">Elétrico</option>
                            <option value="Híbrido">Híbrido</option>
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-600 mb-1.5">Procedência</label>
                        <select
                            className={inputClass('nationality')}
                            name="nationality"
                            value={vehicle.nationality}
                            onChange={handleInputChange}
                            disabled={isReadOnly}
                        >
                            <option value="">Selecione...</option>
                            <option value="Nacional">Nacional</option>
                            <option value="Importado">Importado</option>
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-600 mb-1.5">Município</label>
                        <input
                            className={inputClass('city')}
                            type="text"
                            name="city"
                            placeholder="Ex: São Paulo"
                            value={vehicle.city}
                            onChange={handleInputChange}
                            readOnly={isReadOnly}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-600 mb-1.5">Blindado?</label>
                        <div className="flex gap-2">
                            <button
                                type="button"
                                disabled={isReadOnly}
                                onClick={() => !isReadOnly && setVehicle(prev => ({ ...prev, armored: false }))}
                                className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium border-2 transition-colors ${
                                    !vehicle.armored
                                        ? 'bg-[#389E0D] text-white border-[#389E0D]'
                                        : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'
                                } ${isReadOnly ? 'opacity-70 cursor-not-allowed' : ''}`}
                            >
                                Não
                            </button>
                            <button
                                type="button"
                                disabled={isReadOnly}
                                onClick={() => !isReadOnly && setVehicle(prev => ({ ...prev, armored: true }))}
                                className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium border-2 transition-colors ${
                                    vehicle.armored
                                        ? 'bg-[#389E0D] text-white border-[#389E0D]'
                                        : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'
                                } ${isReadOnly ? 'opacity-70 cursor-not-allowed' : ''}`}
                            >
                                Sim
                            </button>
                        </div>
                    </div>
                </div>

                {/* Optional fields */}
                <div className="mt-6 pt-6 border-t border-gray-100">
                    <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                        Informações adicionais <span className="text-gray-400 font-normal normal-case">(opcionais)</span>
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-600 mb-1.5">Motor</label>
                            <input
                                className={inputClass('engine')}
                                type="text"
                                name="engine"
                                placeholder="Ex: 1.0 Turbo"
                                value={vehicle.engine || ''}
                                onChange={handleInputChange}
                                readOnly={isReadOnly}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-600 mb-1.5">Cilindrada</label>
                            <input
                                className={inputClass('displacement')}
                                type="text"
                                name="displacement"
                                placeholder="Ex: 1000 cc"
                                value={vehicle.displacement || ''}
                                onChange={handleInputChange}
                                readOnly={isReadOnly}
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-600 mb-1.5">Chassi</label>
                            <input
                                className={inputClass('chassi')}
                                type="text"
                                name="chassi"
                                placeholder="Ex: 9BWZZZ377VT004251"
                                maxLength={17}
                                value={vehicle.chassi || ''}
                                onChange={handleInputChange}
                                readOnly={isReadOnly}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Footer */}
            {!isReadOnly && (
                <div className="w-full px-8 py-5 border-t border-gray-100 flex justify-end max-md:px-5">
                    <button
                        onClick={saveVehicle}
                        className="inline-flex items-center gap-2 px-6 py-2.5 bg-[#389E0D] text-white font-medium text-sm rounded-lg hover:bg-green-700 transition-colors shadow-sm"
                    >
                        Prosseguir
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                    </button>
                </div>
            )}
        </div>
    );
}
